// Admin ticket detail + triage (Story #4613, Stream 8c) — mockup 05. Thread +
// public/internal composer + right rail (requester, ticket, relations, escalate,
// ADO sync). Relation/escalate/quarantine actions call the real Stream 6b/6c
// endpoints; every relation mutation sends the loaded ticket's `version` and
// refreshes on a 409 (optimistic lock).
//
// Backend read-contract gaps handled honestly (flagged in the story): there is no
// admin reply/note endpoint, no status/priority/assignee mutation endpoint, and
// the read payload carries no outbox_id/diagnostics/priority — those controls
// render read-only/disabled rather than faking success.

import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Link2, Loader2, RefreshCw, Trash2, Unlink, Zap } from 'lucide-react'
import ConfirmDialog from '../../ui/ConfirmDialog'
import type { AdminSupportTicket, SupportAttachment, SupportEvent } from '../../../types/support'
import {
  ConflictError,
  adminGetTicket,
  adminReply,
  discardAttachment,
  escalateTicket,
  linkTicket,
  markDuplicateOf,
  releaseQuarantine,
  retrySync,
  undoDuplicate,
  unlinkTicket,
} from '../../../services/support'
import { deriveDisplayStatus, formatAedt, sortedEvents } from '../../../utils/supportDisplay'
import { DisplayStatusChip, RequestTypeBadge } from '../SupportBadges'
import RefSearchPicker from './RefSearchPicker'
import ReleaseQuarantineModal from './ReleaseQuarantineModal'

const ESCALATE_COOLDOWN_MS = 24 * 3600 * 1000

interface AdminTicketDetailProps {
  ticketId: string
  isSecurityAdmin: boolean
  onBack: () => void
}

function AdminMessage({ event, ticket }: { event: SupportEvent; ticket: AdminSupportTicket }) {
  const isInternal = event.visibility === 'internal'
  const isRequester = event.event_type === 'requester_message' || event.event_type === 'ticket_created'
  const systemTypes = ['ticket_closed', 'ticket_reopened', 'ticket_escalated', 'ticket_marked_duplicate', 'tickets_linked', 'tickets_unlinked', 'undo_duplicate', 'sync_retry_requested']
  const text = event.event_type === 'ticket_created' ? event.body?.description : event.body?.message

  if (systemTypes.includes(event.event_type) && !text) {
    return (
      <p className="my-2 text-center text-[11.5px] text-slate-500">
        {event.event_type.replace(/_/g, ' ')} · {formatAedt(event.created_at)}
      </p>
    )
  }

  const cls = isInternal
    ? 'border-amber-200 bg-amber-50 border-l-4 border-l-amber-400'
    : isRequester
      ? 'border-iflow-900/10'
      : 'border-iflow-900/10 border-l-4 border-l-iflow-500'

  return (
    <div className={`mb-2.5 rounded-xl border bg-white px-3.5 py-3 ${cls}`}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-bold text-iflow-900">
          {isRequester ? `${ticket.requester_name || 'Requester'}` : event.actor}
          {isInternal && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-amber-700">Internal note</span>}
        </span>
        <span className="text-[11px] text-slate-500">{formatAedt(event.created_at)}</span>
      </div>
      {text && <p className="mt-1.5 whitespace-pre-wrap text-[13px] text-slate-700">{text}</p>}
    </div>
  )
}

export default function AdminTicketDetail({ ticketId, isSecurityAdmin, onBack }: AdminTicketDetailProps) {
  const [ticket, setTicket] = useState<AdminSupportTicket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionNote, setActionNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [composerTab, setComposerTab] = useState<'public' | 'internal'>('public')
  const [draft, setDraft] = useState('')

  const [picker, setPicker] = useState<null | 'duplicate' | 'link'>(null)
  const [releaseFor, setReleaseFor] = useState<SupportAttachment | null>(null)
  const [discardFor, setDiscardFor] = useState<SupportAttachment | null>(null)
  const [releaseInFlight, setReleaseInFlight] = useState(false)

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true)
      setError(null)
      adminGetTicket(ticketId, signal)
        .then((t) => {
          setTicket(t)
          setLoading(false)
        })
        .catch((e) => {
          if ((e as Error)?.name === 'AbortError') return
          setError(e instanceof Error ? e.message : 'Could not load this ticket')
          setLoading(false)
        })
    },
    [ticketId],
  )

  useEffect(() => {
    const ctrl = new AbortController()
    load(ctrl.signal)
    return () => ctrl.abort()
  }, [load])

  // Run a relation/version mutation; refetch on a 409 and prompt a retry.
  const runVersioned = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setActionError(null)
      setActionNote(null)
      try {
        await fn()
        load()
      } catch (e) {
        if (e instanceof ConflictError) {
          load()
          setActionError('This ticket changed under you — it’s been refreshed, please try again.')
        } else {
          setActionError(e instanceof Error ? e.message : 'Action failed')
        }
        throw e
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  async function handleEscalate() {
    if (!ticket) return
    try {
      const r = await escalateTicket(ticket.ticket_id)
      setActionNote(r.sms === 'sent' ? 'Escalated — SMS sent to on-call.' : 'Escalated.')
      load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not escalate')
    }
  }

  async function handleSendReply() {
    if (!ticket || !draft.trim() || busy) return
    setBusy(true)
    setActionError(null)
    setActionNote(null)
    try {
      await adminReply(ticket.ticket_id, draft.trim(), composerTab)
      setDraft('')
      setActionNote(composerTab === 'public' ? 'Reply sent — the requester was notified.' : 'Internal note added.')
      load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not send reply')
    } finally {
      setBusy(false)
    }
  }

  async function handleRetrySync() {
    if (!ticket) return
    // The read payload doesn't expose the outbox_id, so retry is only possible
    // when it happens to be present (defensive) — otherwise surface why.
    const outboxId = (ticket as { outbox_id?: string }).outbox_id
    if (!outboxId) {
      setActionError('Sync retry needs the outbox reference, which isn’t in the ticket payload in this release.')
      return
    }
    try {
      await retrySync(ticket.ticket_id, outboxId)
      setActionNote('Sync retry queued.')
      load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not retry sync')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Loading ticket…
      </div>
    )
  }
  if (error || !ticket) {
    return (
      <div className="py-8">
        <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[12.5px] text-iflow-600">
          <ArrowLeft size={14} /> Back to queue
        </button>
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error || 'Ticket not found'}</p>
      </div>
    )
  }

  const events = sortedEvents(ticket.events)
  const status = deriveDisplayStatus(ticket, ticket.user_id)
  const lastEscalatedMs = ticket.last_escalated_at ? new Date(ticket.last_escalated_at).getTime() : 0
  const escalateGuarded = lastEscalatedMs > 0 && Date.now() - lastEscalatedMs < ESCALATE_COOLDOWN_MS
  const quarantined = (ticket.attachments ?? []).filter((a) => a.scan_status === 'quarantined')
  const linked = ticket.linked_tickets ?? []
  const duplicates = ticket.duplicates ?? []
  const isDuplicate = Boolean(ticket.duplicate_of)
  const relatedIds = [ticket.ticket_id, ...linked, ...duplicates, ...(ticket.duplicate_of ? [ticket.duplicate_of] : [])]

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[12.5px] text-iflow-600 hover:underline">
        <ArrowLeft size={14} /> Back to queue
      </button>
      <h1 className="mt-1.5 text-2xl font-bold text-iflow-900">
        {ticket.ref} — {ticket.subject}
      </h1>
      <div className="mb-4 mt-1 flex flex-wrap items-center gap-2 text-[13px] text-slate-500">
        <RequestTypeBadge type={ticket.request_type} />
        <DisplayStatusChip status={status} />
        <span>· Raised {formatAedt(ticket.created_at)}</span>
      </div>

      {(actionError || actionNote) && (
        <p
          role={actionError ? 'alert' : 'status'}
          className={`mb-3 rounded-lg px-3 py-2 text-[12.5px] ${actionError ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}
        >
          {actionError || actionNote}
        </p>
      )}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Left — thread + composer */}
        <div className="min-w-0 flex-1">
          {events.map((e) => (
            <AdminMessage key={e.seq} event={e} ticket={ticket} />
          ))}

          {/* Quarantined attachments */}
          {quarantined.length > 0 && (
            <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3.5">
              <h2 className="text-[13px] font-semibold text-rose-800">Flagged attachments (security review)</h2>
              <ul className="mt-2 space-y-2">
                {quarantined.map((a) => (
                  <li key={a.attachment_id} className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2 text-[12.5px]">
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{a.filename}</span>
                    <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-rose-700">Quarantined</span>
                    {isSecurityAdmin && (
                      <button
                        type="button"
                        onClick={() => setReleaseFor(a)}
                        className="rounded-md border border-amber-300 px-2 py-1 text-[11.5px] font-semibold text-amber-800 hover:bg-amber-50"
                      >
                        Release…
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDiscardFor(a)}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-2 py-1 text-[11.5px] font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      <Trash2 size={12} /> Discard
                    </button>
                  </li>
                ))}
              </ul>
              {!isSecurityAdmin && (
                <p className="mt-2 text-[11px] text-rose-700">
                  Releasing a quarantined file requires the security-admin role.
                </p>
              )}
            </div>
          )}

          {/* Composer (mockup 05). Public reply -> requester + ADO; internal note
              stays AU-only. Wired to POST /admin/tickets/{id}/reply. */}
          <div className="rounded-xl border border-iflow-900/10 bg-white/80 p-4">
            <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
              <button
                type="button"
                onClick={() => setComposerTab('public')}
                className={`px-3.5 py-1.5 text-[13px] ${composerTab === 'public' ? 'bg-iflow-500 font-semibold text-white' : 'bg-white text-slate-700'}`}
              >
                Public reply
              </button>
              <button
                type="button"
                onClick={() => setComposerTab('internal')}
                className={`border-l border-slate-200 px-3.5 py-1.5 text-[13px] ${composerTab === 'internal' ? 'bg-amber-400 font-semibold text-gray-900' : 'bg-white text-slate-700'}`}
              >
                Internal note
              </button>
            </div>
            <textarea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                composerTab === 'public'
                  ? `Reply to ${ticket.requester_name || 'the requester'} — they’ll be notified in the portal`
                  : 'Internal note — not visible to the requester'
              }
              className="mt-2.5 w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[13px]"
            />
            <div className="mt-2.5 flex items-center gap-2">
              <button
                type="button"
                onClick={handleSendReply}
                disabled={busy || !draft.trim()}
                className="rounded-lg bg-iflow-500 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-iflow-600 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                {busy ? 'Sending…' : composerTab === 'public' ? 'Send reply' : 'Add internal note'}
              </button>
              <span className="text-[12px] text-slate-500">
                {composerTab === 'public'
                  ? ticket.ado_id
                    ? `Mirrored to ADO #${ticket.ado_id}`
                    : 'Will mirror to ADO once synced'
                  : 'Internal — never leaves the portal'}
              </span>
            </div>
          </div>
        </div>

        {/* Right rail */}
        <div className="w-full shrink-0 space-y-4 lg:w-[300px]">
          {/* Requester */}
          <div className="rounded-xl border border-iflow-900/10 bg-white/80 p-4">
            <h2 className="mb-2 text-[14px] font-semibold text-iflow-900">Requester</h2>
            <RailField label="Name" value={ticket.requester_name || ticket.user_id || '—'} />
            {ticket.requester_org && <RailField label="Organisation" value={ticket.requester_org} />}
            {ticket.requester_email && <RailField label="Email" value={ticket.requester_email} />}
          </div>

          {/* Ticket */}
          <div className="rounded-xl border border-iflow-900/10 bg-white/80 p-4">
            <h2 className="mb-2 text-[14px] font-semibold text-iflow-900">Ticket</h2>
            <RailField label="Status" value={<DisplayStatusChip status={status} />} />
            <RailField label="Priority" value={ticket.priority ?? '—'} />
            <RailField label="Assignee" value={ticket.assignee ?? '—'} />

            {/* Escalate — real endpoint, 24h guard */}
            <dt className="mt-2.5 text-[11px] uppercase tracking-wide text-slate-500">Escalate</dt>
            <dd className="mt-1">
              {escalateGuarded ? (
                <span className="inline-flex items-center gap-1 text-[12.5px] text-amber-700">
                  <Zap size={13} /> Escalated {formatAedt(ticket.last_escalated_at)}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleEscalate}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-2.5 py-1 text-[12.5px] font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-60"
                >
                  <Zap size={13} /> Escalate (SMS on-call)
                </button>
              )}
            </dd>

            {/* ADO sync */}
            <dt className="mt-2.5 text-[11px] uppercase tracking-wide text-slate-500">Azure DevOps</dt>
            <dd className="mt-1 text-[12.5px]">
              {ticket.ado_url ? (
                <a href={ticket.ado_url} target="_blank" rel="noreferrer" className="font-semibold text-iflow-600 hover:underline">
                  #{ticket.ado_id} ↗
                </a>
              ) : ticket.ado_id ? (
                <span className="text-slate-700">#{ticket.ado_id}</span>
              ) : (
                <span className="text-slate-400">not synced</span>
              )}
              {ticket.sync_status === 'failed' ? (
                <button
                  type="button"
                  onClick={handleRetrySync}
                  className="ml-2 inline-flex items-center gap-1 font-semibold text-rose-600 hover:underline"
                >
                  <RefreshCw size={12} /> Retry sync now
                </button>
              ) : (
                <span className="ml-2 text-emerald-600">· synced ✓</span>
              )}
            </dd>

            {/* Relations */}
            <dt className="mt-2.5 text-[11px] uppercase tracking-wide text-slate-500">Linked tickets</dt>
            <dd className="mt-1 space-y-1 text-[12.5px]">
              {isDuplicate && (
                <div className="flex items-center justify-between gap-2 rounded bg-violet-50 px-2 py-1">
                  <span className="text-violet-800">Duplicate of another ticket</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void runVersioned(() => undoDuplicate(ticket.ticket_id, ticket.version)).catch(() => {})}
                    className="text-[11.5px] font-semibold text-iflow-700 hover:underline disabled:opacity-60"
                  >
                    Undo
                  </button>
                </div>
              )}
              {linked.map((otherId) => (
                <div key={otherId} className="flex items-center justify-between gap-2">
                  <span className="truncate text-slate-700">{otherId}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void runVersioned(() => unlinkTicket(ticket.ticket_id, otherId, ticket.version)).catch(() => {})}
                    className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-slate-500 hover:text-rose-600 disabled:opacity-60"
                  >
                    <Unlink size={11} /> Unlink
                  </button>
                </div>
              ))}
              {duplicates.length > 0 && (
                <div className="mt-1 rounded bg-slate-50 px-2 py-1 text-[11.5px] text-slate-600">
                  {duplicates.length} ticket{duplicates.length === 1 ? '' : 's'} marked as duplicates of this one
                </div>
              )}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {!isDuplicate && (
                  <button
                    type="button"
                    onClick={() => setPicker('duplicate')}
                    className="rounded-md border border-iflow-200 px-2 py-1 text-[11.5px] font-semibold text-iflow-700 hover:bg-iflow-50"
                  >
                    Mark as duplicate of…
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPicker('link')}
                  className="inline-flex items-center gap-1 rounded-md border border-iflow-200 px-2 py-1 text-[11.5px] font-semibold text-iflow-700 hover:bg-iflow-50"
                >
                  <Link2 size={12} /> Link related ticket…
                </button>
              </div>
            </dd>
          </div>
        </div>
      </div>

      {/* Relation pickers */}
      {picker === 'duplicate' && (
        <RefSearchPicker
          title="Mark as duplicate of…"
          confirmLabel="Mark as duplicate"
          excludeIds={relatedIds}
          onPick={(other) => runVersioned(() => markDuplicateOf(ticket.ticket_id, other.ticket_id, ticket.version))}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'link' && (
        <RefSearchPicker
          title="Link related ticket"
          confirmLabel="Link ticket"
          excludeIds={relatedIds}
          onPick={(other) => runVersioned(() => linkTicket(ticket.ticket_id, other.ticket_id, ticket.version))}
          onClose={() => setPicker(null)}
        />
      )}

      {/* Release-quarantine (security-admin only) */}
      {releaseFor && (
        <ReleaseQuarantineModal
          filename={releaseFor.filename}
          releaseInFlight={releaseInFlight}
          onConfirm={async (justification) => {
            setReleaseInFlight(true)
            try {
              await releaseQuarantine(ticket.ticket_id, releaseFor.attachment_id, justification)
              load()
            } finally {
              setReleaseInFlight(false)
            }
          }}
          onClose={() => setReleaseFor(null)}
        />
      )}

      {/* Discard — async ConfirmDialog (components/ui) that surfaces failures */}
      <ConfirmDialog
        open={Boolean(discardFor)}
        tone="danger"
        title="Discard flagged attachment"
        confirmLabel="Discard permanently"
        message={
          <>
            This permanently deletes <b>{discardFor?.filename}</b> and its stored object. It can’t be undone.
            {releaseInFlight && (
              <span className="mt-2 block rounded bg-amber-50 px-2 py-1 text-amber-800">
                A release for a flagged file is already in progress — discarding now cancels that review.
              </span>
            )}
          </>
        }
        onConfirm={async () => {
          if (!discardFor) return
          await discardAttachment(ticket.ticket_id, discardFor.attachment_id)
          load()
        }}
        onClose={() => setDiscardFor(null)}
      />
    </div>
  )
}

function RailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="mt-2.5 text-[11px] uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-[13px] text-slate-800">{value}</dd>
    </>
  )
}
